import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Minus, Plus, ArrowUpRight, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import ColorPicker from "@/components/ColorPicker";
import AnimatedBackdrop from "@/components/AnimatedBackdrop";
import ApproveButton from "@/components/ApproveButton";
import { useSwipe } from "@/hooks/useSwipe";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { add } = useCart();

  // Gallery derivations (safe pre-p) then swipe hook — hooks must run every render.
  const gallery = (p?.images && p.images.length > 0) ? p.images : (p ? [p.image] : []);
  const hasGallery = gallery.length > 1;
  const prev = () => setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setActiveIdx((i) => (i + 1) % gallery.length);
  const swipe = useSwipe({ onLeft: next, onRight: prev });

  useEffect(() => {
    setP(null);
    setActiveIdx(0);
    api.get(`/products/${slug}`).then((r) => setP(r.data));
    api.get("/products").then((r) => setRelated(r.data.products.filter((x) => x.slug !== slug).slice(0, 3)));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!p) return <div className="pt-40 text-center overline">Loading…</div>;

  const handleAdd = () => {
    add(p, qty);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="pt-24">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Sticky gallery */}
        <div className="bg-[#FAF9F6] lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] flex flex-col p-2 sm:p-3 lg:p-4">
          <div
            className="relative flex-1 min-h-[480px] sm:min-h-[600px] lg:min-h-0 flex items-center justify-center overflow-hidden group touch-pan-y select-none"
            data-testid="gallery-swipe-area"
            {...swipe}
          >
            {gallery.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`${p.name} — view ${i + 1}`}
                data-testid={`product-image-${i}`}
                onClick={() => { if (i === activeIdx) setLightboxOpen(true); }}
                className={`absolute max-w-full max-h-full object-contain cursor-zoom-in transition-opacity duration-700 ease-out ${i === activeIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              />
            ))}
            {/* Expand hint */}
            <button
              data-testid="expand-image-btn"
              onClick={() => setLightboxOpen(true)}
              aria-label="Expand image"
              className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center bg-[#FAF9F6]/85 border border-[#E5E5E5] hover:bg-[#FAF9F6] transition-all duration-300"
            >
              <Expand size={16} strokeWidth={1.25} />
            </button>
            {hasGallery && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={prev}
                  aria-label="Previous image"
                  className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-[#1C1C1C] text-[#FAF9F6] hover:bg-[#C9A961] transition-colors duration-300 shadow-lg"
                >
                  <ChevronLeft size={22} strokeWidth={1.5} />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={next}
                  aria-label="Next image"
                  className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-[#1C1C1C] text-[#FAF9F6] hover:bg-[#C9A961] transition-colors duration-300 shadow-lg"
                >
                  <ChevronRight size={22} strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>
          {hasGallery && (
            <div className="mt-3 flex items-center justify-between" data-testid="gallery-controls">
              <div className="overline text-[#1C1C1C]">
                {String(activeIdx + 1).padStart(2, "0")} <span className="text-[#A8A096]">/ {String(gallery.length).padStart(2, "0")}</span>
              </div>
              <div className="flex gap-2" data-testid="gallery-dots">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-dot-${i}`}
                    onClick={() => setActiveIdx(i)}
                    aria-label={`Show image ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${i === activeIdx ? "w-8 bg-[#1C1C1C]" : "w-4 bg-[#C7C1B4] hover:bg-[#A8A096]"}`}
                  />
                ))}
              </div>
            </div>
          )}
          {hasGallery && (
            <div className={`mt-2 grid gap-2 ${gallery.length <= 4 ? "grid-cols-4" : gallery.length <= 6 ? "grid-cols-6" : "grid-cols-7"}`} data-testid="gallery-thumbs">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveIdx(i)}
                  aria-label={`Show image ${i + 1}`}
                  className={`aspect-[5/4] overflow-hidden bg-[#FAF9F6] border transition-colors duration-300 ${i === activeIdx ? "border-[#1C1C1C]" : "border-[#E5E5E5] hover:border-[#A8A096]"}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Scrolling right pane */}
        <div className="relative px-6 lg:px-16 py-16 lg:py-24 overflow-hidden">
          <AnimatedBackdrop variant="cream" />
          <div className="relative z-10">
          <div className="overline">{p.category}</div>
          <h1 data-testid="product-name" className="font-serif-editorial text-4xl lg:text-6xl mt-4 leading-none">{p.name}</h1>
          <p className="mt-6 italic font-serif-editorial text-xl text-[#5C5C5C]">{p.tagline}</p>

          <div className="mt-10 pb-8 border-b border-[#E5E5E5]">
            {(() => {
              const financingEligible = ["Hyperbaric Therapy", "Red Light Therapy"].includes(p.category);
              return (
                <>
                  <div className="flex items-baseline flex-wrap gap-x-5 gap-y-2">
                    <div data-testid="product-price" className="font-serif-editorial text-4xl">{fmt(p.price)}</div>
                    {financingEligible && (
                      <div data-testid="product-financing" className="text-sm text-[#5C5C5C] leading-tight">
                        <span className="tt-shimmer-gold font-medium">
                          {fmt(p.finance_monthly || Math.round(p.price / 60))}
                        </span>
                        <span className="text-[#A8A096]"> / mo</span>
                        <span className="overline block mt-0.5 text-[#A8A096]">Est. over 60 months</span>
                      </div>
                    )}
                  </div>
                  {financingEligible && (
                    <ApproveButton
                      price={p.price}
                      model={p.name}
                      qty={1}
                      type={p.category}
                      theme="light"
                      className="mt-5"
                    />
                  )}
                </>
              );
            })()}
            <div className="overline mt-3">Concierge installation included</div>
          </div>

          <p className="mt-10 text-base leading-relaxed text-[#1C1C1C]/85">{p.short_description}</p>

          {/* Color picker — shown on hyperbaric chambers */}
          {(p.slug === "hyperbaric-oxygen-chamber" || p.slug === "space-capsule-hyperbaric-chamber") && (
            <ColorPicker defaultColor="Gold" />
          )}

          {/* Add to cart */}
          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center border border-[#E5E5E5]">
              <button data-testid="detail-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F0EFEB] transition-colors duration-300"><Minus size={14} strokeWidth={1.5} /></button>
              <span data-testid="detail-qty-value" className="px-6 text-sm">{qty}</span>
              <button data-testid="detail-qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F0EFEB] transition-colors duration-300"><Plus size={14} strokeWidth={1.5} /></button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={handleAdd} className="tt-btn-primary flex-1">Add to Cart</button>
          </div>

          {/* Benefits */}
          <div className="mt-16 pt-12 border-t border-[#E5E5E5]">
            <div className="overline mb-8">Med Spa Integration</div>
            <ul className="space-y-6">
              {p.benefits.map((b, i) => (
                <li key={i} className="flex gap-6 items-start">
                  <span className="overline text-[#A8A096] pt-1">0{i + 1}</span>
                  <span className="text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Specs */}
          <div className="mt-16 pt-12 border-t border-[#E5E5E5]">
            <div className="overline mb-8">Specifications</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12">
              {p.specs.map((s, i) => (
                <div key={i} className="border-b border-[#E5E5E5] pb-4">
                  <dt className="overline">{s.label}</dt>
                  <dd className="mt-2 font-serif-editorial text-xl">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="overline mb-12">Also considered</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/product/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                <div className="aspect-[4/5] bg-white overflow-hidden border border-[#E5E5E5]">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out" />
                </div>
                <div className="mt-6 flex justify-between items-start">
                  <div>
                    <div className="overline">{r.category}</div>
                    <div className="font-serif-editorial text-xl mt-2">{r.name}</div>
                  </div>
                  <ArrowUpRight size={16} strokeWidth={1.25} className="mt-2 group-hover:rotate-12 transition-transform duration-500" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {lightboxOpen && (
        <Lightbox
          images={gallery}
          startIndex={activeIdx}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
